<template>
  <div v-if="segment" class="w-full h-full">
    <EndUserUploadDrawer v-model:open="showUploadDrawer" />

    <Drawer direction="right" :open="showRenameDrawer" @update:open="(o) => !o && (showRenameDrawer = false)">
      <DrawerContent class="sm:max-w-md">
        <DrawerHeader class="text-left">
          <DrawerTitle>Rename segment</DrawerTitle>
          <DrawerDescription>Update this segment's name</DrawerDescription>
        </DrawerHeader>
        <div class="px-4 flex-1">
          <label class="text-sm font-medium" for="seg-name">Name</label>
          <Input id="seg-name" v-model="renameName" type="text" />
        </div>
        <DrawerFooter class="flex-row justify-end gap-2">
          <Button variant="outline" @click="showRenameDrawer = false">Cancel</Button>
          <Button :disabled="!renameName.trim() || savingName" @click="renameSegment()">Save</Button>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>

    <div class="flex items-start justify-between gap-3 mb-4">
      <div class="min-w-0">
        <h1 class="text-xl font-semibold truncate" :title="segment.name">{{ segment.name || 'Segment' }}</h1>
        <p class="text-sm text-muted-foreground break-all">{{ segmentId }}</p>
      </div>
      <div class="flex items-center gap-2 shrink-0">
        <Button variant="outline" size="sm" @click="openRename()">
          <Pencil class="w-4 h-4" />Edit name
        </Button>
        <Button variant="destructive" size="sm" @click="showDeleteModal = true">
          <Trash2 class="w-4 h-4" />Delete
        </Button>
      </div>
    </div>

    <AlertDialog :open="showDeleteModal" @update:open="(o) => !o && (showDeleteModal = false, deleteError = '')">
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Delete segment?</AlertDialogTitle>
          <AlertDialogDescription>
            This permanently deletes “{{ segment.name || 'this segment' }}”. This cannot be undone.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <!-- A delete is rejected while the segment is still attached to offers; without this
             the dialog just sat there and the failure was invisible. -->
        <p v-if="deleteError" class="text-sm text-destructive">{{ deleteError }}</p>
        <AlertDialogFooter class="gap-2">
          <AlertDialogCancel @click="showDeleteModal = false; deleteError = ''">Cancel</AlertDialogCancel>
          <AlertDialogAction :disabled="deletingSegment" @click="deleteSegment()">Delete</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>

    <section v-if="headline" class="mb-4">
      <div class="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card class="p-4">
          <div class="flex items-center justify-between mb-2">
            <p class="text-xs uppercase tracking-wide text-muted-foreground">Total users</p>
            <Users class="w-4 h-4 text-muted-foreground" />
          </div>
          <p class="text-2xl font-semibold">{{ headline.totalUsers }}</p>
        </Card>
        <Card class="p-4">
          <div class="flex items-center justify-between mb-2">
            <p class="text-xs uppercase tracking-wide text-muted-foreground">Monthly active users</p>
            <Speech class="w-4 h-4 text-muted-foreground" />
          </div>
          <p class="text-2xl font-semibold">{{ headline.activeUsers }}</p>
        </Card>
        <Card class="p-4">
          <div class="flex items-center justify-between mb-2">
            <p class="text-xs uppercase tracking-wide text-muted-foreground">Percentage of active users</p>
            <Percent class="w-4 h-4 text-muted-foreground" />
          </div>
          <p class="text-2xl font-semibold">{{ headline.totalUsers ? ((headline.activeUsers / headline.totalUsers) * 100).toFixed(1) : '0' }}%</p>
        </Card>
        <Card class="p-4">
          <div class="flex items-center justify-between mb-2">
            <p class="text-xs uppercase tracking-wide text-muted-foreground">Number of offers avaliable</p>
            <ShoppingBasket class="w-4 h-4 text-muted-foreground" />
          </div>
          <p class="text-2xl font-semibold">{{ serverItemsLength }}</p>
        </Card>
      </div>
    </section>

    <Card class="mb-4">
      <CardHeader>
        <CardTitle>Users</CardTitle>
        <CardDescription>Users enrolled in this segment</CardDescription>
        <CardAction>
          <Button size="sm" @click="showUploadDrawer = true">
            <User class="w-4 h-4" />Update user tiers via csv
          </Button>
        </CardAction>
      </CardHeader>
      <CardContent>
        <DataTable
          :headers="usersHeaders"
          :items="users"
          :loading="loadingUsers"
          :error="errorUsers"
          @retry="getSegmentUsers(serverOptions3.page - 1, serverOptions3.rowsPerPage)"
          v-model:server-options="serverOptions3"
          :server-items-length="serverItemsLength3"
          searchable
          search-placeholder="Search by user id"
          :search="usersSearch"
          @update:search="usersSearch = $event"
          @search="getSegmentUsers(0, serverOptions3.rowsPerPage)"
          @clear-search="onClearUsersSearch()"
        >
          <template #item-endUserId="{ endUserId }">
            <router-link
              v-if="endUserId"
              :to="`/enrolled-users/users/${endUserId}`"
              class="text-xs font-mono font-medium whitespace-nowrap text-sky-600 dark:text-sky-400 hover:text-sky-500 dark:hover:text-sky-300 hover:underline underline-offset-2 transition-colors"
              @click.stop
            >
              {{ endUserId }}
            </router-link>
            <span v-else class="text-muted-foreground">—</span>
          </template>
          <template #item-createdOn="{ createdOn }">
            <span class="text-sm text-muted-foreground whitespace-nowrap">{{ createdOn ? niceDateTime(createdOn) : '—' }}</span>
          </template>
        </DataTable>
      </CardContent>
    </Card>

    <Card v-if="!showDeactivatedOffers">
      <CardHeader>
        <CardTitle>Available offers for segment</CardTitle>
        <CardDescription>Offers available to users in this segment</CardDescription>
      </CardHeader>
      <CardContent>
        <DataTable
          :headers="headersTable1"
          :items="retailers"
          :loading="loading"
          :error="error"
          @retry="getProgramOffers(serverOptions.page - 1, serverOptions.rowsPerPage)"
          v-model:server-options="serverOptions"
          :server-items-length="serverItemsLength"
          searchable
          search-placeholder="Search by retailer name"
          :search="offersSearch"
          @update:search="offersSearch = $event"
          @search="getProgramOffers(0, serverOptions.rowsPerPage)"
          @clear-search="onClearOffersSearch()"
          @row-click="(row) => $router.push(`/retailer/${row.retailer.id}/offer/${row.offer.id}`)"
        >
          <template #item-id="retailers">
            <CopyId v-if="retailers.id" :value="retailers.id" :max-width="120" />
            <span v-else class="text-muted-foreground">—</span>
          </template>
          <template #item-retailer="retailers">
            <div class="flex items-center gap-2.5">
              <img v-if="retailers.retailer.logo" :src="retailers.retailer.logo" class="w-8 h-8 rounded-md border border-border object-contain shrink-0 bg-white" alt="" />
              <span class="text-sm font-medium truncate" :title="retailers.retailer.retailerName">{{ retailers.retailer.retailerName }}</span>
            </div>
          </template>
          <template v-slot:[`item-offer.country`]="retailers">
            <Badge v-if="retailers.offer.country" variant="secondary">{{ retailers.offer.country }}</Badge>
            <span v-else class="text-muted-foreground">—</span>
          </template>
          <template v-slot:[`item-offer.currency`]="retailers">
            <Badge v-if="retailers.offer.currency" variant="secondary">{{ retailers.offer.currency }}</Badge>
            <span v-else class="text-muted-foreground">—</span>
          </template>
          <template #item-offertype="retailers">
            <Badge variant="secondary">{{ formatOfferType(retailers.offer.offerType) }}</Badge>
          </template>
          <template #item-cashbackAmount="retailers">
            <span class="text-sm tabular-nums whitespace-nowrap">
              {{ retailers.cashbackEarnings?.[0]?.conditions?.[0]?.value?.type === 'PERCENTAGE' ? '' : '£' }}{{ retailers.displayRules?.[0]?.highestCashbackPercentageAmount }}{{ retailers.cashbackEarnings?.[0]?.conditions?.[0]?.value?.type === 'PERCENTAGE' ? '%' : '' }}
            </span>
          </template>
          <template #item-cap="retailers">
            <span v-if="retailers.offer.spendingCapped || retailers.offer.spendingCapAmount > 0" class="text-sm tabular-nums whitespace-nowrap">
              {{ getCurrencySymbol(retailers.offer.currency) + retailers.offer.spendingCapAmount }}
            </span>
            <span v-else class="text-muted-foreground">—</span>
          </template>
          <template #item-active="active">
            <Badge v-if="active.active" variant="success">Live</Badge>
            <Badge v-else variant="warning">Deactivated</Badge>
          </template>
        </DataTable>
      </CardContent>
    </Card>
  </div>
</template>

<script setup></script>

<script>
import { DateTime } from 'luxon'
import {
  Card, CardHeader, CardTitle, CardDescription, CardContent, CardAction,
  Badge,
  Button,
  CopyId,
  Input,
  Drawer, DrawerContent, DrawerHeader, DrawerTitle, DrawerDescription, DrawerFooter,
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription,
  AlertDialogFooter, AlertDialogCancel, AlertDialogAction,
} from '@boogi/ui'
import { User, Users, Speech, Percent, ShoppingBasket, Pencil, Trash2 } from 'lucide-vue-next'
import { DataTable } from '@boogi/ui'
import EndUserUploadDrawer from '@/views/audience/EndUserUpload.vue'

export default {
  name: 'manage-publsihers',
  components: {
    DataTable,
    EndUserUploadDrawer,
    Card, CardHeader, CardTitle, CardDescription, CardContent, CardAction,
    Badge,
    Button,
    CopyId,
    Input,
    Drawer, DrawerContent, DrawerHeader, DrawerTitle, DrawerDescription, DrawerFooter,
    AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription,
    AlertDialogFooter, AlertDialogCancel, AlertDialogAction,
    User, Users, Speech, Percent, ShoppingBasket, Pencil, Trash2,
  },
  props: {},

  data() {
    return {
      segmentId: this.$route.params.segmentId,
      showUploadDrawer: false,
      showDeactivatedOffers: false,
      segment: undefined,
      headline: undefined,

      showRenameDrawer: false,
      renameName: '',
      showDeleteModal: false,
      deleteError: '',
      deletingSegment: false,
      savingName: false,

      // Users enrolled in this segment.
      usersHeaders: [
        { text: 'End user id', value: 'endUserId' },
        { text: 'Added on', value: 'createdOn' },
      ],
      users: [],
      usersSearch: '',
      loadingUsers: true,
      errorUsers: false,
      loading: false,
      error: false,
      serverOptions3: {
        page: 1,
        rowsPerPage: 25,
      },
      serverItemsLength3: undefined,

      offersSearch: '',
      headersTable1: [
        { text: 'Program offer', value: 'id' },
        { text: 'Retailer', value: 'retailer' },
        { text: 'Country', value: 'offer.country' },
        { text: 'Currency', value: 'offer.currency' },
        { text: 'Status', value: 'active' },
        { text: 'Type', value: 'offertype' },
        { text: 'Cashback', value: 'cashbackAmount' },
        { text: 'Spending Cap', value: 'cap' },
      ],
      retailers: [],

      serverOptions: {
        page: 1,
        rowsPerPage: 25,
      },
      successAction: '',
      serverItemsLength: undefined,
    }
  },
  created() {
    this.getCustomerTier()
    this.getCustomerTierHeadlineData()
    this.getProgramOffers(0, 25)
    this.getSegmentUsers(0, 25)
  },
  methods: {
    formatOfferType(t) {
      return ({ CARD_LINKED: 'Card-linked', AFFILIATE: 'Affiliate', IN_STORE: 'In-store', ONLINE: 'Online' })[t] || t
    },

    openRename() {
      this.renameName = this.segment?.name || ''
      this.showRenameDrawer = true
    },

    async deleteSegment() {
      if (this.deletingSegment) return
      this.deletingSegment = true
      this.deleteError = ''
      try {
        const token = localStorage.getItem('token')
        const apiUrl = localStorage.getItem('apiUrl') || process.env.VUE_APP_API_URL
        const response = await this.$apiFetch(
          `${apiUrl}/private/api/client/customer-tier/${this.segmentId}`,
          { method: 'DELETE', headers: { 'Content-Type': 'application/json', Authorization: `${token}` } }
        )
        if (response.ok) {
          this.showDeleteModal = false
          this.$router.push('/enrolled-users/user-segments')
        } else {
          this.deleteError =
            'Could not delete this segment. Remove it from any offers that still use it, then try again.'
        }
      } catch (error) {
        this.deleteError = 'Could not reach the server. Please try again.'
      } finally {
        this.deletingSegment = false
      }
    },

    async renameSegment() {
      if (!this.renameName.trim()) return
      this.savingName = true
      try {
        const token = localStorage.getItem('token')
        const apiUrl = localStorage.getItem('apiUrl') || process.env.VUE_APP_API_URL
        // Update preserves the other fields, so send them alongside the new name.
        const response = await this.$apiFetch(
          `${apiUrl}/private/api/client/customer-tier/${this.segmentId}`,
          {
            method: 'POST',
            body: JSON.stringify({
              name: this.renameName.trim(),
              description: this.segment?.description,
              paidSubscription: this.segment?.paidSubscription,
              price: this.segment?.price,
            }),
            headers: { 'Content-Type': 'application/json', Authorization: `${token}` },
          }
        )
        if (response.ok) {
          this.segment = await response.json()
          this.showRenameDrawer = false
        }
      } catch (error) {
        return
      } finally {
        this.savingName = false
      }
    },

    onClearUsersSearch() {
      this.usersSearch = ''
      this.serverOptions3 = { page: 1, rowsPerPage: this.serverOptions3.rowsPerPage || 25 }
      this.getSegmentUsers(0, this.serverOptions3.rowsPerPage)
    },

    onClearOffersSearch() {
      this.offersSearch = ''
      this.serverOptions = { page: 1, rowsPerPage: this.serverOptions.rowsPerPage || 25 }
      this.getProgramOffers(0, this.serverOptions.rowsPerPage)
    },

    async getSegmentUsers(page, size) {
      const token = localStorage.getItem('token')
      const apiUrl = localStorage.getItem('apiUrl') || process.env.VUE_APP_API_URL
      this.loadingUsers = true
      this.errorUsers = false
      try {
        const searchParam = this.usersSearch ? `&search=${encodeURIComponent(this.usersSearch)}` : ''
        const response = await this.$apiFetch(
          `${apiUrl}/private/api/client/customer-tier/${this.segmentId}/users?page=${page}&size=${size}${searchParam}`,
          {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `${token}`,
            },
          }
        )

        if (response.ok) {
          var object = await response.json()
          this.serverItemsLength3 = object.totalElements
          this.users = object.content
        } else {
          this.errorUsers = true
        }
      } catch (e) {
        this.errorUsers = true
      } finally {
        this.loadingUsers = false
      }
    },

    getCurrencySymbol(currencyCode, locale = 'en-US') {
      return (0)
        .toLocaleString(locale, {
          style: 'currency',
          currency: currencyCode,
          minimumFractionDigits: 0,
          maximumFractionDigits: 0,
        })
        .replace(/\d/g, '')
        .trim() // strip digits, leave symbol
    },

    niceDateTime(dateObject) {
      return DateTime.fromMillis(dateObject).toLocaleString(DateTime.DATETIME_MED)
    },

    async getCustomerTier() {
      try {
        const token = localStorage.getItem('token')
        const apiUrl = localStorage.getItem('apiUrl') || process.env.VUE_APP_API_URL

        this.loading = true

        const response = await this.$apiFetch(
          `${apiUrl}/private/api/client/customer-tier/${this.segmentId}`,
          {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `${token}`,
            },
          }
        )

        if (response.ok) {
          var object = await response.json()
          this.segment = object
        }
      } catch (error) {
        return
      }
    },

    async getCustomerTierHeadlineData() {
      try {
        const token = localStorage.getItem('token')
        const apiUrl = localStorage.getItem('apiUrl') || process.env.VUE_APP_API_URL

        this.loading = true

        const response = await this.$apiFetch(
          `${apiUrl}/private/api/client/customer-tier/${this.segmentId}/topline`,
          {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `${token}`,
            },
          }
        )

        if (response.ok) {
          var object = await response.json()
          this.headline = object
        }
      } catch (error) {
        return
      }
    },

    async getProgramOffers(page, size) {
      const token = localStorage.getItem('token')
      const apiUrl = localStorage.getItem('apiUrl') || process.env.VUE_APP_API_URL

      this.loading = true
      this.error = false
      try {
        const searchParam = this.offersSearch ? `&search=${encodeURIComponent(this.offersSearch)}` : ''
        const response = await this.$apiFetch(
          `${apiUrl}/private/api/client/activated-offer/customer-tier/${this.segmentId}?page=${page}&size=${size}${searchParam}`,
          {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `${token}`,
            },
          }
        )

        if (response.ok) {
          var object = await response.json()

          this.serverItemsLength = object.totalElements
          this.retailers = object.content
        } else {
          this.error = true
        }
      } catch (e) {
        this.error = true
      } finally {
        this.loading = false
      }
    },
  },
  computed: {},
  watch: {
    serverOptions: {
      deep: true,
      handler(val) {
        this.getProgramOffers(val.page - 1, val.rowsPerPage)
      },
    },
    serverOptions3: {
      deep: true,
      handler(val) {
        this.getSegmentUsers(val.page - 1, val.rowsPerPage)
      },
    },
  },
}
</script>
